/**
 * Profile Pic With Progress Component
 * Circular profile image with optional progress ring and label/bar
 * Used in left pane and mobile sidebar; profile page uses plain avatar
 *
 * @author Varun Pandey
 */

import React from 'react';
import { User } from 'lucide-react';
import { cn } from '@/utils/cn';

const CIRCLE_R = 16;
const CIRCUM = 2 * Math.PI * CIRCLE_R;
const VIEWBOX = 36;

export interface ProfilePicWithProgressProps {
  src: string | null | undefined;
  alt?: string;
  percent: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  imageClassName?: string;
  /** When true, show percentage label and horizontal bar below circle (left pane / mobile sidebar) */
  showLabelAndBar?: boolean;
}

const sizeClasses = {
  sm: 'w-16 h-16',
  md: 'w-24 h-24',
  lg: 'w-32 h-32 lg:w-44 lg:h-44',
};

export const ProfilePicWithProgress: React.FC<ProfilePicWithProgressProps> = ({
  src,
  alt = 'Profile',
  percent,
  size = 'md',
  className,
  imageClassName,
  showLabelAndBar = false,
}) => {
  const clamped = Math.min(100, Math.max(0, percent));
  const strokeDash = (clamped / 100) * CIRCUM;

  return (
    <div className={cn('relative flex-shrink-0 flex flex-col items-center', className)}>
      <div className={cn('relative flex-shrink-0', sizeClasses[size])}>
        <div
          className={cn(
            'absolute inset-0 rounded-full overflow-hidden border-2 border-[#1C1C1E] bg-[#2C2C2E] flex items-center justify-center',
            sizeClasses[size],
            imageClassName
          )}
        >
          {src ? (
            <img src={src} alt={alt} className="w-full h-full object-cover" />
          ) : (
            <User className="h-[40%] w-[40%] text-[#8E8E93] opacity-50" />
          )}
        </div>
        <svg
          className="absolute inset-0 w-full h-full rotate-90 pointer-events-none"
          viewBox={`0 0 ${VIEWBOX} ${VIEWBOX}`}
          aria-hidden
        >
          <circle
            cx={VIEWBOX / 2}
            cy={VIEWBOX / 2}
            r={CIRCLE_R}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="text-[#3A3A3C]"
          />
          <circle
            cx={VIEWBOX / 2}
            cy={VIEWBOX / 2}
            r={CIRCLE_R}
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            className="text-[#F33363] transition-[stroke-dasharray] duration-500"
            style={{
              strokeDasharray: `${strokeDash} ${CIRCUM}`,
            }}
          />
        </svg>
        {showLabelAndBar && (
          <>

            {/* Label on bottom center of circle border, with border (segment behind it is not part of progress) */}
            <div className="absolute -bottom-1 left-6 min-w-[2.5rem] rounded-full  bg-[#F33363] px-3  text-center shadow-sm">
              <span className="text-[10px] font-bold text-white tabular-nums">{clamped}%</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
